import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {ClientDgSearchResults} from "../models/datagrid-search-results/client.dg-search-results.model";
import {Client, ClientReportType} from "../models/client.model";
import {VetClinic} from "../models/vet-clinic.model";
import {Partner} from "../models/partner.model";
import {DocumentsResponse} from "../models/documents-response.model";
import {ClientUserCarePlanSettings} from "../models/client-user-care-plan-settings.model";

/**
 * Serwis warstwy danych zarządzający klientami B2B w systemie.
 * Obsługuje operacje CRUD dla kont firmowych, integracje płatnicze i komunikacyjne per tenant,
 * oraz zasilanie tabel DataGrid na poziomie administracyjnym.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientsService implements DataGridServiceInterface<Observable<ClientDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę klientów dla tabeli DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ClientDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ClientDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Client(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje klientów na podstawie zaawansowanych filtrów tekstowych.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ClientDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/clients/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ClientDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Client(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje zmiany w profilu Klienta (np. zmiana danych rejestrowych NIP, nazwy sieci).
   */
  updateClient(clientEdited: Client): Observable<Client> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/clients', clientEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy nowego Klienta B2B w systemie.
   */
  addClient(client: Client): Observable<Client> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/clients`, client).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dziennik audytu (historię zmian) dla danego profilu Klienta.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa klienta.
   */
  deleteClient(clientUUid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/clients/delete/` + clientUUid).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera płaską, niepaginowaną listę wszystkich Klientów.
   */
  listAllClients(): Observable<Client[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/clients-all', {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Client(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukiwarka dla kont Klientów, często używana w globalnych paskach wyszukiwania.
   */
  searchForClientByPhrase(phrase: string, partnersIncluded: boolean = false) {
    const body = {
      phrase: phrase,
      partnersIncluded: partnersIncluded
    };
    return this.apiService.post<VetineoApiResponse>('/dashboard/clients/by-phrase', body, {}).pipe(take(1), map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver(new Client(), [], vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Pobiera zagregowaną listę wszystkich Klientów i Partnerów.
   */
  listAllClientsAndPartners(): Observable<Client[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/c-p-all`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Client(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełną listę fizycznych placówek weterynaryjnych w całym systemie.
   */
  listAllClinics(): Observable<VetClinic[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics-all`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę klinik przypisanych do klienta, które mają aktywowaną funkcję komunikacji.
   */
  listAllClinicsWithMessagesEnabled(vClientUUID: string): Observable<VetClinic[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics-all-with-messages-enabled/${vClientUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę wszystkich Partnerów w systemie.
   */
  listAllPartners() {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partners-all`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Partner(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera profil konkretnej Kliniki, na podstawie kontekstu zalogowanego użytkownika.
   */
  getClinicInfo(): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vet-clinics/dt').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera profil Kliniki w kontekście konkretnej rezerwacji wizyty.
   */
  getClinicInfoForVetRes(uuid: string): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/dt/vet-res/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera szczegółowe dane pojedynczego Klienta z wykorzystaniem UUID.
   */
  getClientByUUID(vClientUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/${vClientUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza synchronizację danych z bramki płatności Przelewy24.
   */
  importDataFromP24(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/p24/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera szczegółowe dane pojedynczego Klienta z wykorzystaniem numerycznego ID.
   */
  getClientById(vClientId: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/for-user/${vClientId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę dokumentów powiązanych z danym Klientem.
   */
  getDocuments(vClientUUID): Observable<DocumentsResponse> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vclients/docs/${vClientUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new DocumentsResponse(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę autoryzowanych nazw nadawców SMS dla konkretnej kliniki.
   */
  getSenders(vetClinicUUID): Observable<string[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/sms-server-senders/client/${vetClinicUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Aktualizuje poświadczenia integracji z zewnętrzną bramką SMS (SMS Serwer) dla danego klienta.
   */
  updateSMSSerwerCredentials(vClientUUID: string, smsSerwerUser: string, smsSerwerPassword: string, smsSerwerSender: string, smsSerwerEnabled: boolean): Observable<VetClinic> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/sms-server-update', {
      uuid: vClientUUID,
      smsSerwerUser: smsSerwerUser,
      smsSerwerPassword: smsSerwerPassword,
      smsSerwerSender: smsSerwerSender,
      smsSerwerEnabled: smsSerwerEnabled
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Odblokowuje moduł Planów Opieki (Wellness Plans) dla określonego klienta.
   */
  turnOnVetineoCarePlanForClient(uuid: any): Observable<Client> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clients/care-plan/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dedykowaną konfigurację wyświetlania planów opieki w interfejsie konkretnego Klienta.
   */
  getClientUserCarePlanSettings(uuid: any): Observable<ClientUserCarePlanSettings> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/client-user-care-plan-settings/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ClientUserCarePlanSettings>(new ClientUserCarePlanSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje ustawienia planów opieki na koncie Klienta.
   */
  updateClientUserCarePlanSettings(clientUserCarePlanSettings: ClientUserCarePlanSettings): Observable<ClientUserCarePlanSettings> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/client-user-care-plan-settings', clientUserCarePlanSettings).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ClientUserCarePlanSettings>(new ClientUserCarePlanSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Ustawia lub modyfikuje poziom prowizji od sprzedanych voucherów dla danego Klienta.
   */
  updateVoucherProvisionLevel(vClientUUID: string, level: string) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/client-voucher-provision-level', {
      'uuid': vClientUUID,
      'level': level
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return "OK";
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Ustawia konfigurację raportów wysyłanych drogą mailową do klienta.
   * @param requestObj obiekt konfiguracji raportów klienta.
   * Przesłanie nieaktywnej flagi sendEmailWithClientReports oznacza wyłączenie funkcjonalności raportowania dla klienta
   */
  updateReportConfiguration(requestObj: {
    uuid: string,
    clientReportsEmails?: string,
    vClientReportType?: ClientReportType,
    sendEmailWithClientReports: boolean
  }) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/client-reports', requestObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Client>(new Client(), vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  getCsv(from,to) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/global-report/${from}/${to}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.report;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
